import { useQuery } from "@tanstack/react-query";
import i18n from "@/i18n";
import { api } from "@/lib/api";
import type { components } from "@jbnl/api-client";

export type Inschrijving = components["schemas"]["WedstrijddagInschrijvingDto"];
export type Beschikbaarheid = components["schemas"]["WedstrijddagBeschikbaarheidDto"];

/**
 * The three answers, from the generated enum rather than hand-mirrored: reordering it in C#
 * would otherwise relabel the badges here without anything failing.
 */
export const ANSWER = { geen: 0, voorkeur: 1, geenVoorkeur: 2 } as const satisfies Record<
  string,
  components["schemas"]["DatePreferenceAnswer"]
>;

/** Unpaged and narrowed in the browser, so the facets can count what they would leave. */
function useDeelname<T>(
  soort: "inschrijvingen" | "beschikbaarheden",
  route:
    | "/api/beheer/wedstrijddagen/{wedstrijddagId}/inschrijvingen"
    | "/api/beheer/wedstrijddagen/{wedstrijddagId}/beschikbaarheden",
  wedstrijddagId: string | undefined,
) {
  return useQuery({
    queryKey: ["beheer", "wedstrijddag", soort, wedstrijddagId ?? ""] as const,
    enabled: Boolean(wedstrijddagId),
    queryFn: async () => {
      const { data, error, response } = await api.GET(route, {
        params: { path: { wedstrijddagId: wedstrijddagId! } },
      });
      if (!data) {
        throw new Error(
          (error as { title?: string } | undefined)?.title ??
            i18n.t(`deelname:errors.${soort}`, { status: response.status }),
        );
      }
      return data as T[];
    },
  });
}

export const useInschrijvingen = (wedstrijddagId: string | undefined) =>
  useDeelname<Inschrijving>(
    "inschrijvingen",
    "/api/beheer/wedstrijddagen/{wedstrijddagId}/inschrijvingen",
    wedstrijddagId,
  );

export const useBeschikbaarheden = (wedstrijddagId: string | undefined) =>
  useDeelname<Beschikbaarheid>(
    "beschikbaarheden",
    "/api/beheer/wedstrijddagen/{wedstrijddagId}/beschikbaarheden",
    wedstrijddagId,
  );
